import * as Sentry from '@sentry/react-router';
import type { GraphQLError } from 'graphql';
import type { ClientError } from 'graphql-request';

function mapGraphQLError({
  error,
  operationName,
  extra,
}: {
  error: unknown;
  operationName?: string;
  extra?: Record<string, unknown>;
}) {
  const originalError = (error as ClientError)?.response?.errors?.[0]
    ?.extensions?.originalError as GraphQLError;

  const validErrorMessage =
    originalError?.message || (error as Error)?.message || 'Unknown';

  const extraDetails = extra ? ` Extra Details: ${JSON.stringify(extra)}` : '';

  const errorMessage = operationName
    ? `${operationName} Error: ${validErrorMessage}.${extraDetails}`
    : `Error: ${validErrorMessage}.${extraDetails}`;

  return errorMessage;
}

function logSentryError({
  name,
  message,
  stack,
  source,
  tags,
  extra,
  level = 'error',
}: LogSentryErrorInterface): void {
  Sentry?.withScope((scope) => {
    const error = new CustomError({
      name,
      message,
      stack,
    });

    scope.setLevel(level);
    scope.setTag('source', source);

    if (tags) {
      tags.forEach(({ name: tagName, value }) => scope.setTag(tagName, value));
    }

    if (extra) {
      scope.setExtra('additionalData', extra);
    }

    Sentry.captureException(error);
  });
}

export interface CustomErrorInterface {
  name: Error['name'];
  message: Error['message'];
  stack?: Error['stack'];
}

class CustomError extends Error {
  public constructor({ name, message, stack }: CustomErrorInterface) {
    super(message);
    this.name = name;
    if (stack) {
      this.stack = stack;
    }
  }
}

interface TagInterface {
  name: string;
  value: string;
}

interface LogSentryErrorInterface {
  name: Error['name'];
  message: Error['message'];
  source: 'client' | 'server';
  stack?: Error['stack'];
  tags?: TagInterface[];
  extra?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
}

export { mapGraphQLError, logSentryError };
